import { useMsal } from "@azure/msal-react";
import React, { useState, useEffect, useRef } from 'react';
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { models, IEmbedConfiguration } from "powerbi-client";
import { PowerBIEmbed } from 'powerbi-client-react';
import { IPowerBIEmbedParams } from "../types/IPowerBIEmbedParams";
import { IPowerBIAnalyticsConfig } from "../types/IPowerBIAnalyticsConfig";
import CarouselScrollButton from "../../repertoire/components/carouselScrollButton/CarouselScrollButton";
import ContributorsDataViewButton from "../../repertoire/works/components/contributorsDataViewButton/ContributorsDataViewButton";
import IMembersPortalComponentDataItem from "../../redux/types/IMembersPortalComponentDataItem";


export interface IAnalyticsPageProps {
    isCRMIntegrationEnabled: boolean;
    powerPortalURL: string;
    currentUserGuid: string;
    currentUserNameNumber: string;
    powerBIEmbedParams: IPowerBIEmbedParams;
    powerBIConfiguration: IPowerBIAnalyticsConfig[];
    statementsAnalyticsPageData: IMembersPortalComponentDataItem;
    getUserCRMValues: () => void;
    getPowerBiEmbed: (reportId: string, queryParameter: string) => IPowerBIEmbedParams;
    getPowerBIConfiguration: () => IPowerBIAnalyticsConfig;

}

const MOBILE_BREAKPOINT = 768;
const PBI_SCHEMA = "http://powerbi.com/product/schema";
const LANG_MAP: Record<string, string> = { "en-GB": "en-IE", "en-US": "en-IE", "fr": "fr-FR" };

const getLanguagePref = (): string => {
    const pref = JSON.parse(localStorage.getItem('userLanguagePreferences') || '"en-IE"');
    return LANG_MAP[pref] || pref;
};

const AnalyticsPage = (props: IAnalyticsPageProps & { dispatch?: any }) => {
    const { instance, accounts } = useMsal();
    const [language, setLanguage] = useState<string>(getLanguagePref);

    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= MOBILE_BREAKPOINT);
    const reportRef = useRef<any>(null);
    const isMobileRef = useRef<boolean>(window.innerWidth <= MOBILE_BREAKPOINT);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            isMobileRef.current = mobile;
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const yearGroups: Record<number, string[]> = {};
    for (let i = 0; i < 4; i++) {
        let month = currentMonth - i;
        let year = currentYear;
        if (month <= 0) { month += 12; year -= 1; }
        if (!yearGroups[year]) yearGroups[year] = [];
        yearGroups[year].push(new Date(year, month - 1, 1).toLocaleString('en-CA', { month: 'long' }));
    }

    const sixMonthsAgo = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 6, 1));
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999));

    const usageDateFilter = {
        $schema: `${PBI_SCHEMA}#advanced`,
        target: { table: "dim_usage_date", column: "Date" },
        logicalOperator: "And",
        conditions: [
            { operator: "GreaterThanOrEqual", value: sixMonthsAgo.toISOString() },
            { operator: "LessThanOrEqual", value: todayUTC.toISOString() }
        ],
        filterType: models.FilterType.Advanced
    };

    const distributionPeriodHierarchyFilter = {
        $schema: `${PBI_SCHEMA}#hierarchy`,
        target: [
            { table: "dim_date", hierarchy: "Year Month Hierarchy", hierarchyLevel: "Year" },
            { table: "dim_date", hierarchy: "Year Month Hierarchy", hierarchyLevel: "Month" }
        ],
        filterType: 9,
        hierarchyData: Object.entries(yearGroups).map(([year, months]) => ({
            operator: "Inherited",
            value: Number(year),
            children: months.map(m => ({ operator: "Selected", value: m }))
        }))
    };

    const defaultSlicers = [
        {
            selector: {
                $schema: `${PBI_SCHEMA}#slicerTargetSelector`,
                target: { table: "dim_date", hierarchy: "Year Month Hierarchy", hierarchyLevel: "Year" }
            },
            state: { filters: [distributionPeriodHierarchyFilter] }
        },
        {
            selector: {
                $schema: `${PBI_SCHEMA}#slicerTargetSelector`,
                target: { table: "dim_usage_date", column: "Date" }
            },
            state: { filters: [usageDateFilter] }
        }
    ];

    // Mobile only: set Distribution Period slicer state post-load
    // (slicers in embed config is not applied on MobilePortrait layout)
    const handleReportLoaded = async () => {
        if (!isMobileRef.current || !reportRef.current) return;
        try {
            const pages = await reportRef.current.getPages();
            const activePage = pages.find((p: any) => p.isActive);
            if (!activePage) return;
            const visuals = await activePage.getVisuals();
            for (const visual of visuals.filter((v: any) => v.type === 'slicer')) {
                try {
                    const state = await visual.getSlicerState();
                    if (state.targets?.some((t: any) => t.table === 'dim_date' && t.hierarchy === 'Year Month Hierarchy')) {
                        await visual.setSlicerState(defaultSlicers[0].state);
                        break;
                    }
                } catch {}
            }
        } catch {}
    };

    useEffect(() => {
        // The language dropdown calls window.location.reload() on change,
        // so same-tab changes are handled by the page reload.
        // This listener handles cross-tab language changes only.
        const onLanguageChange = () => setLanguage(getLanguagePref());
        window.addEventListener('storage', onLanguageChange);
        return () => window.removeEventListener('storage', onLanguageChange);
    }, []);

    React.useEffect(() => {
        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, [])

    React.useEffect(() => {
        if (!props.currentUserGuid) {
            props.getUserCRMValues();
        }
    }, [props.currentUserGuid])

    React.useEffect(() => {
        if (!props.powerBIConfiguration) {
            props.getPowerBIConfiguration();
        }
        else if(props.powerBIConfiguration && !props.powerBIEmbedParams){
            props.getPowerBiEmbed(props.powerBIConfiguration[0].reportId, props.powerBIConfiguration[0].queryParameter);
            
        }
    }, [props.powerBIConfiguration, props.powerBIEmbedParams])

    let embedConfiguration: IEmbedConfiguration;

    if(props.powerBIEmbedParams) {

        if(props.powerBIEmbedParams.embedToken && props.powerBIEmbedParams.embedReports && props.powerBIEmbedParams.embedReports.length > 0)
        {
            embedConfiguration = {
                accessToken: props.powerBIEmbedParams.embedToken.token,
                embedUrl: props.powerBIEmbedParams.embedReports[0].embedUrl,
                id: props.powerBIEmbedParams.embedReports[0].reportId,
                tokenType: models.TokenType.Embed,
                type: 'report',
                slicers: defaultSlicers as any,
                filters: [usageDateFilter] as any,
                settings: {
                    layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Master,
                    localeSettings: {
                        language: language,
                        formatLocale: language
                    },
                    panes:{
                        filters: { visible: false },
                        pageNavigation: { visible: false }
                    }
                }
            };
        }
    }
    else{
        embedConfiguration = {
            embedUrl: undefined,
            id: undefined,
            tokenType: models.TokenType.Embed,
            type: 'report',
            accessToken: undefined,
            settings: {
                layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Master,
                panes:{
                    filters: { visible: false },
                    pageNavigation: { visible: false }
                }
            }
        };
    }        

    const onClick = (reportId:string) => {
        let reportDetail = props.powerBIConfiguration.find(item => item.reportId === reportId);
        props.getPowerBiEmbed(reportDetail.reportId, reportDetail.queryParameter);
    }

    const renderReportButtons = () => {

        if(props.powerBIConfiguration && props.powerBIEmbedParams){
            return props.powerBIConfiguration.map((item, index) => (
                <ContributorsDataViewButton
                    key={index}
                    text={props.statementsAnalyticsPageData.fields.find((v, i, a) => String(v.name) === item.reportName)?.data || item.reportName}
                    onClick={() => onClick(item.reportId)}
                    active={props.powerBIEmbedParams?.embedReports[0].reportId === item.reportId}
                />
            ));
        }        
    };    

    let powerbiReport;
    if(embedConfiguration){
        powerbiReport = (
            <PowerBIEmbed
                key={`${isMobile ? 'mobile' : 'desktop'}-${language}-${props.powerBIEmbedParams?.embedToken?.tokenId ?? ''}`}
                embedConfig={embedConfiguration}
                getEmbeddedComponent={(embedded) => { reportRef.current = embedded; }}
                eventHandlers={new Map<string, any>([['loaded', handleReportLoaded]])}
            />
        );
    }

     let headerText = "";

    if (props.statementsAnalyticsPageData && props.statementsAnalyticsPageData.fields.length > 0) {
        const pageDescriptionField = props.statementsAnalyticsPageData.fields.find((v, i, a) => String(v.name) === "pageDescription");
        headerText = pageDescriptionField ? pageDescriptionField.data : "";
        }

    return (
        <div>            
            <div className={"powerbiReportContainer"} >
                {powerbiReport}
            </div>
        </div>
    );
}

export default AnalyticsPage;